import React from 'react';
import { View, StyleSheet } from 'react-native-web';
import { Dropdown, TextInput, FormField } from '@dupe/shared';

/**
 * SettingsNavBar - Filter bar for creator search
 *
 * Uses composition instead of conditional components.
 * Each filter type is explicitly rendered based on its key.
 */
const SettingsNavBar = ({ settings, settingOptions, onSettingChange }) => {
    // Handler for text input value changes (updates state, doesn't trigger search)
    const handleTextChange = (text, settingKey) => {
        const syntheticEvent = {
            target: { value: text }
        };
        onSettingChange(syntheticEvent, settingKey, false); // false = don't search yet
    };

    // Handler for search trigger (blur or Enter key)
    const handleTextSearch = (settingKey) => {
        const syntheticEvent = {
            target: { value: settings[settingKey] || '' }
        };
        onSettingChange(syntheticEvent, settingKey, true); // true = trigger search
    };

    // Factory function to create onChange handlers for each dropdown
    const handleDropdownChange = (settingKey) => (value) => {
        // Convert new Dropdown API to parent's expected format
        const syntheticEvent = {
            target: { value }
        };
        onSettingChange(syntheticEvent, settingKey);
    };

    return (
        <View style={styles.settingsNavBar}>
            <View style={styles.settingsContainer}>
                {Object.keys(settings).map((settingKey) => {
                    // Determine if this is a text input field
                    const isTextInput = !settingOptions[settingKey] ||
                                       settingOptions[settingKey].length === 0;

                    // Text input fields (e.g., Username search)
                    if (isTextInput) {
                        return (
                            <FormField key={settingKey} label={settingKey}>
                                <TextInput
                                    value={settings[settingKey] || ''}
                                    onChangeText={(text) => handleTextChange(text, settingKey)}
                                    onBlur={() => handleTextSearch(settingKey)}
                                    onSubmit={() => handleTextSearch(settingKey)}
                                    placeholder={`Enter ${settingKey.toLowerCase()}...`}
                                />
                            </FormField>
                        );
                    }

                    // Multi-select dropdown with search (Countries)
                    if (settingKey === 'Countries') {
                        return (
                            <FormField key={settingKey} label={settingKey}>
                                <Dropdown
                                    value={settings[settingKey]}
                                    options={settingOptions[settingKey]}
                                    onChange={handleDropdownChange(settingKey)}
                                    multiSelect={true}
                                    enableSearch={true}
                                    placeholder="All"
                                    searchPlaceholder="Search countries..."
                                    noResultsText="No countries found"
                                />
                            </FormField>
                        );
                    }

                    // Single-select dropdown with search (Aesthetic)
                    if (settingKey === 'Aesthetic') {
                        return (
                            <FormField key={settingKey} label={settingKey}>
                                <Dropdown
                                    value={settings[settingKey]}
                                    options={settingOptions[settingKey]}
                                    onChange={handleDropdownChange(settingKey)}
                                    multiSelect={false}
                                    enableSearch={true}
                                    placeholder="All"
                                    searchPlaceholder="Search aesthetics..."
                                />
                            </FormField>
                        );
                    }

                    // Default: single-select dropdown without search
                    return (
                        <FormField key={settingKey} label={settingKey}>
                            <Dropdown
                                value={settings[settingKey]}
                                options={settingOptions[settingKey]}
                                onChange={handleDropdownChange(settingKey)}
                                multiSelect={false}
                                enableSearch={false}
                                placeholder="Select..."
                            />
                        </FormField>
                    );
                })}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    settingsNavBar: {
        backgroundColor: 'white',
        borderBottomWidth: 1,
        borderBottomColor: '#b8cbf5',
        paddingVertical: 15,
        paddingHorizontal: 20,
        marginBottom: 20,
    },
    settingsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
    },
});

export default SettingsNavBar;
